import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useComments } from 'entities/comment';
import { useEditorContext } from 'app/state/contexts/EditorContext';
import { useEditorLiveSection } from 'app/state/contexts/EditorUiContext';
import { useSidebar } from 'app/state/contexts/SidebarContext';
import { useEditorNavigate, useEditorSearch } from 'app/router/params';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { resolveCommentCard, scrollToCommentAnchor } from './comment-jump';
import { CommentItem } from './types';

const COMMENTS_PANEL = 5;

export const CommentJump = () => {
  const { t } = useTranslation('notifications');
  const { goToCommentId } = useEditorSearch();
  const navigate = useEditorNavigate();
  const { superId: sectionSuperId } = useEditorLiveSection();
  const { comments, isCommentsLoading, refetchComments } = useComments(sectionSuperId) as {
    comments: CommentItem[] | undefined;
    isCommentsLoading: boolean;
    refetchComments: () => Promise<{ data?: CommentItem[]; isError: boolean }>;
  };
  const { shadowHost } = useEditorContext();
  const { setIsSidebarOpen, setActiveComment } = useSidebar();
  const [pendingScroll, setPendingScroll] = useState<{ card: CommentItem } | null>(null);
  const handledParam = useRef(false);
  const latestRequest = useRef<string | null>(null);

  useEffect(() => {
    if (goToCommentId) setIsSidebarOpen(COMMENTS_PANEL);
  }, [goToCommentId, setIsSidebarOpen]);

  const jumpTo = useCallback(
    (card: CommentItem) => {
      setActiveComment(card);
      setPendingScroll({ card });
    },
    [setActiveComment]
  );

  useEffect(() => {
    if (!goToCommentId) {
      handledParam.current = false;
      return;
    }
    if (handledParam.current || !shadowHost || !sectionSuperId || isCommentsLoading) return;
    handledParam.current = true;
    latestRequest.current = goToCommentId;

    const card = resolveCommentCard(comments, goToCommentId);
    navigate({ replace: true, search: (prev) => ({ ...prev, goToCommentId: undefined }) });

    if (card) {
      jumpTo(card);
      return;
    }

    void refetchComments().then(({ data, isError }) => {
      if (latestRequest.current !== goToCommentId) return;

      const refetched = resolveCommentCard(data, goToCommentId);
      if (refetched) {
        jumpTo(refetched);
        return;
      }
      if (!isError) {
        dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: t('comment-not-found'), removeTime: 6000 });
      }
    });
  }, [goToCommentId, comments, isCommentsLoading, sectionSuperId, shadowHost, navigate, refetchComments, jumpTo, t]);

  useEffect(() => {
    if (!pendingScroll || !shadowHost) return;
    return scrollToCommentAnchor(shadowHost, pendingScroll.card);
  }, [pendingScroll, shadowHost]);

  return null;
};
